import React, { useMemo } from 'react'
import orderBy from 'lodash/orderBy'
import { ContextCard } from 'transfix-ui/components/ContextCard'
import { Box } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IMotorCarrierApplication, IMotorCarrierMembership } from 'apps/fleet-planner/types'
import { ActionList, ActionListItem, TIconName } from '../action-list'

type TCarrierStateDeprecated =
  | 'not_started'
  | 'upload_documents'
  | 'add_references'
  | 'in_review'
  | 'active'
  | 'banned_status'
  | 'needs_permission'
  | 'insurance_expired'
  | 'contact_suppport'
  | 'finish_setup'

type TCarrierState =
  | 'not_started'
  | 'in_review'
  | 'active'
  | 'banned_status'
  | 'needs_permission'
  | 'insurance_expired'
  | 'contact_suppport'
  | 'finish_setup'
  | 'missing_information'

const ACTIVATION_STATUS_MESSAGE_DEPRECATED: Record<TCarrierStateDeprecated, string> = {
  not_started: 'Application not started',
  finish_setup: 'Finish setup',
  upload_documents: 'Next: Upload documents',
  add_references: 'Next: Add references',
  in_review: 'Your application is being reviewed by Transfix',
  active: 'Your carrier is setup to book loads',
  banned_status: 'Your carrier was not approved to book loads.',
  insurance_expired:
    'Your insurance is expired. Please send your up-to-date insurance certificate to ',
  needs_permission: 'You need permission to access this carrier.',
  contact_suppport:
    "There is an issue with this carrier's account setup. Please contact customer support",
}

const ACTIVATION_STATUS_MESSAGE: Record<TCarrierState, string> = {
  not_started: 'Application not started',
  finish_setup: 'Finish setup',
  in_review: 'Your application is being reviewed by Transfix',
  active: 'Your carrier is setup to book loads',
  banned_status: 'Your carrier was not approved to book loads.',
  insurance_expired:
    'Your insurance is expired. Please send your up-to-date insurance certificate to ',
  needs_permission: 'You need permission to access this carrier.',
  contact_suppport:
    "There is an issue with this carrier's account setup. Please contact customer support",
  missing_information: 'Provide Missing Information',
}

const getActivationStatusHighway = (
  carrierApplication: IMotorCarrierApplication | undefined | null,
  membership: IMotorCarrierMembership,
  isFmsHighwayCarrierOnboardingEnabled: boolean
): { message: string; icon: TIconName; component?: JSX.Element; disabled?: boolean } => {
  const { hasInsurance, hasSignedBca, hasUploadedDocuments, hasW9, state } =
    carrierApplication || {}

  /**
   * If for some reason the object is null,
   * it likely means there's an issue with the imported carrier data
   */
  if (carrierApplication == null) {
    return {
      message: ACTIVATION_STATUS_MESSAGE.contact_suppport,
      icon: 'help',
      disabled: true,
    }
  }

  if (membership === null) {
    return {
      message: ACTIVATION_STATUS_MESSAGE.needs_permission,
      icon: 'help',
      disabled: true,
    }
  }

  if (
    (state === 'in_review' || state === 'application_submitted') &&
    hasSignedBca &&
    hasUploadedDocuments &&
    hasW9 &&
    hasInsurance
  ) {
    return {
      message: ACTIVATION_STATUS_MESSAGE.in_review,
      icon: '75',
      disabled: true,
    }
  }

  if (state === 'activated') {
    return {
      message: ACTIVATION_STATUS_MESSAGE.active,
      icon: '100',
      disabled: true,
    }
  }

  if (
    state &&
    ['cancelled', 'conditional', 'probation', 'do_not_use', 'out_of_service'].includes(state)
  ) {
    return {
      message: ACTIVATION_STATUS_MESSAGE.banned_status,
      icon: 'warning',
      component: (
        <ContextCard triggerText=' Learn more'>
          <div className='mb2'>
            Your carrier wasn’t approved due to your company’s FMCSA rating, safety record, or for
            another reason. Please contact our Compliance team at{' '}
            <a href='mailto: compliance@transfix.io'>compliance@transfix.io</a> for more
            information.
          </div>
        </ContextCard>
      ),
      disabled: true,
    }
  }

  if (
    state === 'expired_insurance' &&
    hasSignedBca &&
    (hasInsurance === false || hasW9 === false)
  ) {
    return {
      message: ACTIVATION_STATUS_MESSAGE.missing_information,
      icon: 'warning',
    }
  }

  if (state === 'expired_insurance' && hasSignedBca && hasW9 === false && hasInsurance === false) {
    return {
      message: ACTIVATION_STATUS_MESSAGE.missing_information,
      icon: 'warning',
    }
  }
  return {
    message: isFmsHighwayCarrierOnboardingEnabled
      ? ACTIVATION_STATUS_MESSAGE.finish_setup
      : ACTIVATION_STATUS_MESSAGE.not_started,
    icon: '0',
  }
}

const getActivationStatusDeprecated = (
  carrierApplication: IMotorCarrierApplication | undefined | null,
  membership: IMotorCarrierMembership,
  isFmsHighwayCarrierOnboardingEnabled: boolean
): { message: string; icon: TIconName; component?: JSX.Element; disabled?: boolean } => {
  /**
   * If for some reason the object is null,
   * it likely means there's an issue with the imported carrier data
   */
  if (carrierApplication == null) {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.contact_suppport,
      icon: 'help',
      disabled: true,
    }
  }

  if (membership === null) {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.needs_permission,
      icon: 'help',
      disabled: true,
    }
  }

  const { hasSignedBca, hasUploadedDocuments, state } = carrierApplication || {}

  if (
    (state === 'in_review' || state === 'application_submitted' || state === 'pending') &&
    hasSignedBca &&
    hasUploadedDocuments
  ) {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.in_review,
      icon: '75',
      disabled: true,
    }
  }
  if (state === 'activated') {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.active,
      icon: '100',
      disabled: true,
    }
  }
  if (
    state &&
    [
      'out_of_service',
      'conditional',
      'cancelled',
      'probation',
      'do_not_use',
      'cannot-use',
      'inactive',
      'other',
    ].includes(state)
  ) {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.banned_status,
      icon: 'warning',
      component: (
        <ContextCard triggerText=' Learn more'>
          <div className='mb2'>
            Your carrier wasn’t approved due to your company’s FMCSA rating, safety record, or for
            another reason. Please contact our Compliance team at{' '}
            <a href='mailto: compliance@transfix.io'>compliance@transfix.io</a> for more
            information.
          </div>
        </ContextCard>
      ),
      disabled: true,
    }
  }
  if (state === 'expired_insurance') {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.insurance_expired,
      icon: 'warning',
      component: <a href='mailto: compliance@transfix.io'>compliance@transfix.io</a>,
      disabled: true,
    }
  }
  if (hasSignedBca && hasUploadedDocuments === false) {
    return {
      message: ACTIVATION_STATUS_MESSAGE_DEPRECATED.upload_documents,
      icon: '50',
    }
  }
  return {
    message: isFmsHighwayCarrierOnboardingEnabled
      ? ACTIVATION_STATUS_MESSAGE_DEPRECATED.finish_setup
      : ACTIVATION_STATUS_MESSAGE_DEPRECATED.not_started,
    icon: '0',
  }
}

export const CarrierSetupContainer = () => {
  const { trackEvent } = useFMSAnalytics()
  const { userMotorCarriers = [], isUserMotorCarriersLoading } = useMembershipContext()
  const isFmsHighwayCarrierOnboardingEnabled = useFeatureFlag('fmsHighwayCarrierOnboarding')
  const isFmsHighwayMilestoneFourEnabled = useFeatureFlag('fmsHighwayMilestoneFour')
  const getActivationStatus = isFmsHighwayMilestoneFourEnabled
    ? getActivationStatusHighway
    : getActivationStatusDeprecated

  const createActivateHandler = (applicationId?: number) => () => {
    if (applicationId) {
      window.open(`${__ROOT__}/fms/activate/${applicationId}`)
      trackEvent('Setup Guide', 'CLICKED_ON_CARRIER_FROM_GET_SET_UP_TO_BOOK_LOADS_MODAL', {
        applicationId,
      })
    }
  }

  const sortedUserMotorCarriers = useMemo(
    () => orderBy(userMotorCarriers, mc => mc?.motorCarrier?.name || '', ['asc']),
    [userMotorCarriers]
  )

  return (
    <Box mr={-3} ml={-3} data-test='carrier-setup-container'>
      <ActionList>
        {isUserMotorCarriersLoading && (
          <>
            <ActionListItem loading />
            <ActionListItem loading />
          </>
        )}
        {!isUserMotorCarriersLoading &&
          sortedUserMotorCarriers.map(userMotorCarrier => {
            const { carrierApplication, membership, motorCarrier } = userMotorCarrier || {}
            const { name = '' } = motorCarrier || {}
            const { id: carrierApplicationId } = carrierApplication || {}
            const clickHandler = createActivateHandler(carrierApplicationId)
            const { component, disabled, icon: iconName, message: subtitle } = getActivationStatus(
              carrierApplication,
              membership,
              isFmsHighwayCarrierOnboardingEnabled
            )
            if (subtitle === ACTIVATION_STATUS_MESSAGE.contact_suppport)
              trackEvent('Setup Guide', 'VIEWED_ERROR_IN_SETUP_GUIDE')
            return (
              <ActionListItem
                disabled={disabled}
                key={carrierApplicationId}
                title={name}
                subtitle={subtitle}
                iconName={iconName}
                onClick={clickHandler}
                subtitleDetailsComponent={component}
              />
            )
          })}
      </ActionList>
    </Box>
  )
}
